import React, { useEffect, useState } from 'react';
import { clsx } from 'clsx';
import { KnowledgeSourceStatus } from '../../types';
import { formatElapsed } from './format';

interface StatusChipProps {
  status: KnowledgeSourceStatus;
  /** ISO timestamp the current indexing run started, for the elapsed counter. */
  since?: string;
  errorMessage?: string;
}

const LABELS: Record<KnowledgeSourceStatus, string> = {
  uploading: 'Uploading',
  not_indexed: 'Not indexed',
  indexing: 'Indexing',
  indexed: 'Indexed',
  // Says what is happening AND implies why it is harmless. "Updating" would promise
  // the content is changing, which is exactly what has not happened yet — the new
  // copy is invisible until it is judged, and may be thrown away.
  checking: 'Checking size',
  // "Not added" rather than "Rejected", and deliberately not the specific reason.
  // The chip is 32 units wide and there are three reasons; the one that matters to
  // the customer at a glance is that this site is not in their chatbot. The reason
  // itself is on the line below, in full, with a way through to the detail.
  rejected: 'Not added',
  failed: 'Failed',
  removing: 'Removing',
};

const STYLES: Record<KnowledgeSourceStatus, string> = {
  uploading: 'bg-sky-50 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300',
  not_indexed: 'bg-amber-50 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  // Rust for work in progress, following clipgen's own pulsing accent for the
  // same idea. Was blue, the one cool colour in the set.
  indexing: 'bg-primary-50 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300',
  indexed: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  // Neutral, NOT the rust of `indexing`. Rust is the accent for work that is changing
  // the chatbot's content; this window changes nothing — the site answers from the
  // previous copy throughout and the candidate may be discarded. Grey with a spinner
  // reads as "busy, benign", which is exactly the state.
  checking: 'bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-gray-300',
  // Red, like `failed`: both are terminal and neither put anything in the knowledge
  // base. Amber would group it with `not_indexed`, which is a benign staged state
  // one button press from being fixed, and the words are close enough already.
  rejected: 'bg-red-50 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  failed: 'bg-red-50 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  removing: 'bg-red-50 text-red-700 dark:bg-red-900/40 dark:text-red-300 line-through',
};

export default function StatusChip({ status, since, errorMessage }: StatusChipProps) {
  // Ticks only while something is actually running. `checking` counts: the elapsed
  // figure is what tells the customer the row is not simply stuck, which is the
  // whole complaint C13 records.
  const [, setTick] = useState(0);
  const isRunning = status === 'indexing' || status === 'checking';

  useEffect(() => {
    if (!isRunning || !since) return;
    const timer = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(timer);
  }, [isRunning, since]);

  const elapsed = isRunning && since ? formatElapsed(since) : '';

  return (
    <span
      title={status === 'failed' || status === 'rejected' ? errorMessage : undefined}
      className={clsx(
        'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap',
        STYLES[status]
      )}
    >
      {isRunning && (
        <span className="w-2.5 h-2.5 border border-current border-t-transparent rounded-full animate-spin" />
      )}
      {status === 'indexed' && <span className="w-1.5 h-1.5 rounded-full bg-current" />}
      {LABELS[status]}
      {elapsed && <span className="tabular-nums opacity-70">{elapsed}</span>}
    </span>
  );
}
